import React from 'react';
import { Check, X, MessageSquare, Bot, Users, BarChart3, Zap, Shield, Star, Instagram, Phone, Globe, TrendingUp, Clock, Target } from 'lucide-react';

const OdooCRM: React.FC = () => {
  const serviceAreas = [
    {
      icon: <MessageSquare size={32} />,
      title: "Integración Multi-Canal",
      description: "Conectamos WhatsApp, Instagram, formularios web y otros canales digitales en una plataforma unificada. Centraliza todas las conversaciones en un solo lugar.",
      benefit: "Control total de todos los puntos de contacto sin perder ninguna consulta o mensaje importante."
    },
    {
      icon: <Bot size={32} />,
      title: "Asistencia Automatizada con IA",
      description: "Chatbots inteligentes responden automáticamente las preguntas frecuentes iniciales, entregan información básica y califican prospectos 24/7.",
      benefit: "Respuesta inmediata a consultas, incluso fuera del horario laboral, mejorando la experiencia del prospecto."
    },
    {
      icon: <Users size={32} />,
      title: "Gestión Inteligente de Prospectos",
      description: "CRM Odoo centraliza toda la información: datos de contacto, historial de conversaciones, intereses y estado del proceso comercial.",
      benefit: "Seguimiento completo de cada prospecto sin información duplicada o perdida entre canales."
    },
    {
      icon: <Target size={32} />,
      title: "Derivación Inteligente",
      description: "Cuando el chatbot identifica una consulta compleja o un prospecto calificado, deriva automáticamente la conversación a un ejecutivo comercial humano.",
      benefit: "Los ejecutivos se enfocan en prospectos calificados, optimizando su tiempo y aumentando conversiones."
    },
    {
      icon: <BarChart3 size={32} />,
      title: "Análisis y Reportes",
      description: "Dashboards en tiempo real con métricas de respuesta, conversión, canales más efectivos, y rendimiento del equipo comercial.",
      benefit: "Decisiones basadas en datos para optimizar estrategias de captación y mejorar resultados."
    },
    {
      icon: <Zap size={32} />,
      title: "Automatización Comercial",
      description: "Flujos automatizados para seguimiento, recordatorios, envío de información y nurturing de prospectos según su etapa en el proceso.",
      benefit: "Proceso comercial estandarizado que no depende de la memoria del equipo, sin prospectos olvidados."
    }
  ];

  const plans = [
    {
      name: "Starter",
      price: "$450.000",
      period: "/mes",
      description: "Ideal para iniciar con automatización comercial",
      popular: false,
      features: [
        { text: "1 canal integrado (WhatsApp o Instagram)", included: true },
        { text: "Chatbot básico con respuestas FAQ", included: true },
        { text: "CRM Odoo con hasta 1.000 contactos", included: true },
        { text: "2 usuarios del sistema", included: true },
        { text: "Reportes básicos mensuales", included: true },
        { text: "Integración con formularios web", included: false },
        { text: "Flujos de automatización avanzados", included: false },
        { text: "Capacitación del equipo", included: false },
        { text: "Soporte prioritario", included: false }
      ],
      buttonText: "Comenzar con Starter",
      buttonClass: "btn-secondary"
    },
    {
      name: "Professional",
      price: "$850.000",
      period: "/mes",
      description: "La solución completa para instituciones en crecimiento",
      popular: true,
      features: [
        { text: "3 canales integrados (WhatsApp, Instagram, Web)", included: true },
        { text: "Chatbot IA avanzado con aprendizaje", included: true },
        { text: "CRM Odoo con hasta 5.000 contactos", included: true },
        { text: "5 usuarios del sistema", included: true },
        { text: "Reportes detallados semanales", included: true },
        { text: "Integración completa con formularios", included: true },
        { text: "5 flujos de automatización personalizados", included: true },
        { text: "Capacitación inicial (4 horas)", included: true },
        { text: "Soporte prioritario", included: true }
      ],
      buttonText: "Más Popular",
      buttonClass: "btn-primary"
    },
    {
      name: "Enterprise",
      price: "$1.450.000",
      period: "/mes",
      description: "Solución empresarial con máxima personalización",
      popular: false,
      features: [
        { text: "Canales ilimitados + WhatsApp Business API", included: true },
        { text: "Chatbot IA premium con NLP avanzado", included: true },
        { text: "CRM Odoo contactos ilimitados", included: true },
        { text: "Usuarios ilimitados", included: true },
        { text: "Reportes personalizados en tiempo real", included: true },
        { text: "Integraciones API personalizadas", included: true },
        { text: "Flujos de automatización ilimitados", included: true },
        { text: "Capacitación continua mensual", included: true },
        { text: "Soporte dedicado 24/7", included: true }
      ],
      buttonText: "Contratar Enterprise",
      buttonClass: "btn-secondary"
    }
  ];

  const benefits = [
    {
      icon: <Clock size={24} />,
      title: "Respuesta Inmediata",
      description: "Reduce tiempo de primera respuesta de horas a segundos"
    },
    {
      icon: <TrendingUp size={24} />,
      title: "Mayor Conversión",
      description: "Aumenta tasas de conversión hasta en 40% con seguimiento automatizado"
    },
    {
      icon: <Shield size={24} />,
      title: "Datos Seguros",
      description: "Información centralizada y protegida sin compartir con terceros"
    },
    {
      icon: <Users size={24} />,
      title: "Equipo Eficiente",
      description: "Ejecutivos enfocados en prospectos calificados de alto valor"
    }
  ];

  const channels = [
    {
      icon: <Phone size={24} />,
      name: "WhatsApp",
      description: "Integración completa con WhatsApp Business API"
    },
    {
      icon: <Instagram size={24} />,
      name: "Instagram",
      description: "Mensajes directos automatizados y sincronizados"
    },
    {
      icon: <Globe size={24} />,
      name: "Formularios Web",
      description: "Captura automática desde tu sitio web"
    },
    {
      icon: <MessageSquare size={24} />,
      name: "Chat en Vivo",
      description: "Widget de chat integrado en tu sitio"
    }
  ];

  const useCases = [
    {
      title: "Instituciones Educativas",
      description: "Gestiona consultas de programas académicos, procesos de admisión y seguimiento a prospectos estudiantiles desde el primer contacto hasta la matrícula.",
      results: "40% más conversiones, 70% menos tiempo de respuesta"
    },
    {
      title: "Centros de Capacitación",
      description: "Automatiza respuestas sobre cursos disponibles, horarios, precios y facilita el registro. Seguimiento post-curso para nuevas inscripciones.",
      results: "3x más consultas atendidas, 50% reducción carga administrativa"
    },
    {
      title: "Universidades Corporativas",
      description: "Centraliza solicitudes de capacitación interna, gestiona catálogos de cursos y coordina programas de formación empresarial.",
      results: "Gestión unificada, reportes ejecutivos en tiempo real"
    }
  ];

  return (
    <section id="odoo-crm" className="odoo-crm-landing">
      {/* Hero Section */}
      <div className="crm-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-badge">
                <Star size={16} />
                Partners Oficiales Odoo
              </div>
              <h1 className="hero-title">
                CRM y Canales Digitales
                <span className="highlight">Powered by Odoo</span>
              </h1>
              <p className="hero-description">
                Centraliza WhatsApp, Instagram y formularios web en un CRM unificado. 
                Automatiza respuestas con IA y mejora tus conversiones con seguimiento inteligente.
              </p>
              <div className="hero-cta">
                <a href="#contacto" className="btn btn-primary btn-lg">
                  Solicitar Demo Gratuita
                </a>
                <a href="#planes" className="btn btn-secondary btn-lg">
                  Ver Planes
                </a>
              </div>
            </div>
            <div className="hero-right">
              <div className="hero-stats">
                <div className="stat-card">
                  <div className="stat-number">70%</div>
                  <div className="stat-label">Menos tiempo de respuesta</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">40%</div>
                  <div className="stat-label">Más conversiones</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">24/7</div>
                  <div className="stat-label">Atención automatizada</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Channels Section */}
      <div className="channels-section">
        <div className="container">
          <div className="section-header">
            <h2>Todos tus canales en un solo lugar</h2>
            <p className="section-subtitle">
              Integración nativa con las plataformas que tus prospectos ya usan
            </p>
          </div>
          <div className="channels-grid">
            {channels.map((channel, index) => (
              <div key={index} className="channel-card">
                <div className="channel-icon">{channel.icon}</div>
                <h4>{channel.name}</h4>
                <p>{channel.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Service Areas */}
      <div className="container">
        <div className="service-areas">
          <div className="section-header">
            <h2>Cómo funciona nuestra solución</h2>
            <p className="section-subtitle">
              Sistema completo de gestión comercial para instituciones educativas
            </p>
          </div>
          
          <div className="service-grid">
            {serviceAreas.map((area, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {area.icon}
                </div>
                <h4>{area.title}</h4>
                <p className="service-description">{area.description}</p>
                <div className="service-benefit">
                  <strong>Beneficio:</strong> {area.benefit}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits */}
        <div className="benefits-section">
          <h3>Beneficios clave para tu institución</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Use Cases */}
        <div className="use-cases-section">
          <div className="section-header">
            <h2>Casos de uso en educación</h2>
            <p className="section-subtitle">
              Soluciones probadas para diferentes tipos de instituciones
            </p>
          </div>
          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card">
                <h4>{useCase.title}</h4>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-results">
                  <Check size={20} className="check-icon" />
                  <span>{useCase.results}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Odoo Partner Section */}
        <div className="odoo-partner-section">
          <div className="partner-content">
            <div className="partner-badge">
              <Shield size={32} />
            </div>
            <h3>Partners Oficiales de Odoo</h3>
            <p>
              Como partners certificados de Odoo, implementamos soluciones CRM de clase mundial 
              adaptadas específicamente para instituciones educativas en Colombia. Contamos con 
              la experiencia y certificación para maximizar el potencial de esta poderosa plataforma.
            </p>
            <ul className="partner-benefits">
              <li><Check size={20} /> Implementación certificada y soporte especializado</li>
              <li><Check size={20} /> Actualizaciones y mejoras continuas incluidas</li>
              <li><Check size={20} /> Personalización según las necesidades de tu institución</li>
              <li><Check size={20} /> Capacitación completa para tu equipo comercial</li>
            </ul>
          </div>
        </div>

        {/* Pricing Plans */}
        <div id="planes" className="pricing-section">
          <div className="section-header">
            <h2>Planes y Precios</h2>
            <p className="section-subtitle">
              Elige el plan que mejor se adapte al tamaño y necesidades de tu institución
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && (
                  <div className="popular-badge">
                    <Star size={16} />
                    Recomendado
                  </div>
                )}
                
                <div className="pricing-header">
                  <h4>{plan.name}</h4>
                  <div className="price">
                    <span className="amount">{plan.price}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>

                <div className="features-list">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="feature-item">
                      {feature.included ? (
                        <Check size={20} className="feature-check" />
                      ) : (
                        <X size={20} className="feature-cross" />
                      )}
                      <span className={feature.included ? '' : 'feature-disabled'}>
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="pricing-footer">
                  <a href="#contacto" className={`btn ${plan.buttonClass} btn-block`}>
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="pricing-note">
            <p>💡 Todos los planes incluyen migración de datos y configuración inicial sin costo adicional</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="final-cta">
          <div className="cta-content">
            <h3>¿Listo para transformar tu gestión comercial?</h3>
            <p>
              Agenda una demo personalizada y descubre cómo podemos ayudarte a centralizar 
              tus canales digitales, automatizar respuestas y aumentar tus conversiones.
            </p>
            <div className="cta-buttons">
              <a href="#contacto" className="btn btn-primary btn-lg">
                Solicitar Demo Gratuita
              </a>
              <a href="https://wa.me/56992235151" target="_blank" rel="noopener noreferrer" className="btn btn-accent btn-lg">
                <Phone size={20} />
                Contactar por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OdooCRM;
